import _ from 'underscore';
import Bluebird from 'bluebird';

import { priorityLevel } from './const';
import type {
  IAcquireLockOptions,
  ILogger,
  IPopupLockEntry,
  IPopupWaitCallback,
  IPopupWaitEntry,
  IWindowWithPopupManager,
} from './types';

export class PopupManager {
  gate: boolean;

  lock: IPopupLockEntry[];

  logger: ILogger | null;

  timer: number | null;

  waitList: IPopupWaitEntry[];

  constructor() {
    this.lock = [];
    this.waitList = [];
    this.gate = false;
    this.timer = null;
    this.logger = null;
  }

  async acquireLock({
    key,
    wait = false,
    priority = priorityLevel.default,
  }: IAcquireLockOptions): Promise<boolean> {
    if (!this.validateKey(key)) {
      return false;
    }

    // if gate is set, wait for the gate to be cleared
    if (this.getGate()) {
      return this.getWaitLockPromise(key, priority);
    }

    // if timer is set, wait for the timer to be cleared
    if (this.getTimer()) {
      return this.getWaitLockPromise(key, priority);
    }

    // acquire lock if lock is empty
    if (_.isEmpty(this.lock)) {
      this.lock.push({ key, force: false });
      this.logPopupManagerState('Acquired lock');
      return true;
    }

    // if lock is not empty, wait for the lock to be released
    if (wait) {
      return this.getWaitLockPromise(key, priority);
    }

    this.logPopupManagerState('Lock is not empty');
    return false;
  }

  forceAcquireLock(key?: string): boolean {
    if (!this.validateKey(key)) {
      return false;
    }

    this.lock.unshift({ key, force: true });
    this.logPopupManagerState('Force acquired lock');
    return true;
  }


  async releaseLock(key: string): Promise<boolean> {
    const idx = _.findIndex(this.lock, (item: IPopupLockEntry) => item.key === key);
    if (idx !== -1) {
      // remove the key from lock
      this.lock = _.filter(this.lock, (item: IPopupLockEntry) => item.key !== key);
      this.logPopupManagerState('Released lock');

      // pop the wait list
      await this.popWaitList();

      return true;
    }

    return false;
  }

  getWaitLockPromise(key: string, priority: number): Promise<boolean> {
    return new Bluebird((resolve) => {
      this.waitLock({
        key,
        priority,

        callback: async (): Promise<void> => {
          resolve(await this.acquireLock({ key }));
        },
      });
    });
  }

  waitLock({
    key,
    priority = priorityLevel.default,

    callback = async (): Promise<void> => {},
  }: {
    key: string;
    priority?: number;
    callback?: IPopupWaitCallback;
  }): void {
    this.waitList.push({ key, priority, callback });
    this.waitList.sort((a, b) => a.priority - b.priority);
  }


  async popWaitList(): Promise<boolean> {
    if (_.isEmpty(this.lock) && !_.isEmpty(this.waitList)) {
      const { callback } = this.waitList.shift() as IPopupWaitEntry;
      // call the callback to resolve the promise
      await callback();

      return true;
    }

    return false;
  }


  async clearWaitList(): Promise<void> {
    await Bluebird.all(this.waitList.map(async ({ callback }) => {
      // call the callback to reject the promise
      await callback();
    }));
    this.waitList = [];
  }

  getGate(): boolean {
    return this.gate;
  }

  setGate(): void {
    this.gate = true;
  }


  async clearGate(): Promise<void> {
    // pop the wait list, and clear the wait list and the gate
    this.logPopupManagerState('Clear gate');
    this.gate = false;
    await this.popWaitList();
    await this.clearWaitList();
    this.logPopupManagerState('After clear gate');
  }

  getTimer(): number | null {
    return this.timer;
  }

  setTimer(timeout: number): void {
    if (this.timer) {
      this.clearTimer();
    }

    this.timer = setTimeout(async () => {
      // pop the wait list, and clear the wait list and timer
      this.clearTimer();
      await this.popWaitList();
      await this.clearWaitList();
    }, timeout) as unknown as number;
  }

  clearTimer(): void {
    clearTimeout(this.timer as number);
    this.timer = null;
  }

  validateKey(key?: string): key is string {
    if (_.isEmpty(key)) {
      return false;
    }

    return true;
  }

  setLogger(logger: ILogger | null): void {
    this.logger = logger;
  }

  log(message: string): void {
    if (this.logger && _.isFunction(this.logger.signal)) {
      this.logger.signal(message);
    }
  }

  logPopupManagerState(prefix = ''): void {
    const gateState = `gate: ${this.getGate()}`;
    const lockState = `popups: ${this.lock.length} | system-initiated-popups: ${this.lock.filter(item => item.force === false).length} | user-initiated-popups: ${this.lock.filter(item => item.force === true).length} | popupsKeys: ${this.lock.map(item => item.key)}`;
    const waitListState = `waitListLength: ${this.waitList.length} | waitListKeys: ${this.waitList.map(item => item.key)}`;
    this.log(`${prefix} | ${gateState} | ${lockState} | ${waitListState}`);
  }
}

export const popupManager = new PopupManager();

// use for debugging
(window as unknown as IWindowWithPopupManager).popupManager = popupManager;
